/**
 * IFRAP-AI - Télécommande Gamma par email
 * Page accessible via un lien dans les emails CyberAgnès.
 * Permet de saisir un sujet et recevoir une présentation Gamma par email.
 */

import React, { useState } from 'react';
import { Presentation, Send, Loader2, Check, FileText } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api/client';

const ADMIN_EMAILS = ['jbgiraud@mac.com', 'averdier@ifrap.org'];

const GammaRemotePage = () => {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email') || '';
  
  const [email, setEmail] = useState(emailParam);
  const [prompt, setPrompt] = useState('');
  const [contentText, setContentText] = useState('');
  const [contentTitle, setContentTitle] = useState('');
  const [mode, setMode] = useState('gamma'); // 'gamma' | 'report' | 'transform'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const isValidEmail = ADMIN_EMAILS.includes(email);

  const handleSubmit = async () => {
    if (!isValidEmail || loading) return;
    
    // Transform mode: submit content directly
    if (mode === 'transform') {
      if (!contentText.trim()) return;
      setLoading(true);
      setError('');
      setResult(null);
      try {
        const { data } = await api.post('/api/reports/transform-to-gamma-remote', {
          content: contentText.trim(),
          title: contentTitle.trim() || undefined,
          email: email,
        });
        setResult(data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Erreur lors de la conversion');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // Generate modes
    if (!prompt.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const endpoint = mode === 'gamma' ? '/api/reports/gamma-only' : '/api/reports/email-request';
      const { data } = await api.post(endpoint, {
        prompt: prompt.trim(),
        email: email,
        export_gamma: mode === 'report',
      });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] to-[#0a0a1a] flex items-center justify-center p-4">
      <div className="w-full max-w-lg" data-testid="gamma-remote-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-purple-600/20 text-purple-300 px-4 py-2 rounded-full text-sm mb-4">
            <Presentation className="w-4 h-4" />
            Télécommande CyberAgnès
          </div>
          <h1 className="text-2xl font-bold text-white">Générer à distance</h1>
          <p className="text-gray-400 text-sm mt-2">Recevez un rapport ou une présentation Gamma par email</p>
        </div>

        {/* Form */}
        {!result ? (
          <div className="bg-[#0f0f20] rounded-2xl border border-gray-800 p-6 space-y-5">
            {/* Email */}
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Votre email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="averdier@ifrap.org"
                className={`w-full bg-[#1a1a2e] border rounded-lg px-4 py-3 text-sm text-white outline-none ${
                  email && !isValidEmail ? 'border-red-500' : 'border-gray-700 focus:border-purple-500'
                }`}
                data-testid="gamma-email-input"
              />
              {email && !isValidEmail && (
                <p className="text-red-400 text-xs mt-1">Adresse non autorisée. Utilisez votre email IFRAP.</p>
              )}
            </div>

            {/* Mode */}
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Type de génération</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setMode('gamma')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-xs font-medium transition-colors ${
                    mode === 'gamma' ? 'bg-purple-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  <Presentation className="w-4 h-4" /> Prompt → Gamma
                </button>
                <button
                  onClick={() => setMode('report')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-xs font-medium transition-colors ${
                    mode === 'report' ? 'bg-[#000091] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  <FileText className="w-4 h-4" /> Rapport + Gamma
                </button>
                <button
                  onClick={() => setMode('transform')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-xs font-medium transition-colors ${
                    mode === 'transform' ? 'bg-purple-800 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  <Send className="w-4 h-4" /> Contenu → Gamma
                </button>
              </div>
            </div>

            {/* Prompt (for gamma/report modes) */}
            {mode !== 'transform' && (
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Sujet</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ex: Impact des 35 heures sur la compétitivité française"
                className="w-full bg-[#1a1a2e] border border-gray-700 rounded-lg px-4 py-3 text-sm text-white outline-none resize-none focus:border-purple-500"
                rows={3}
                disabled={loading}
                data-testid="gamma-prompt-input"
              />
            </div>
            )}

            {/* Content (for transform mode) */}
            {mode === 'transform' && (
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Titre (optionnel)</label>
              <input
                type="text"
                value={contentTitle}
                onChange={(e) => setContentTitle(e.target.value)}
                placeholder="Ex: Analyse du budget 2026"
                className="w-full bg-[#1a1a2e] border border-gray-700 rounded-lg px-4 py-2.5 text-sm text-white outline-none mb-3 focus:border-purple-500"
                disabled={loading}
              />
              <label className="text-sm text-gray-400 mb-1.5 block">Votre contenu</label>
              <textarea
                value={contentText}
                onChange={(e) => setContentText(e.target.value)}
                placeholder="Collez ici votre texte, article, notes... Il sera transformé en présentation Gamma aux couleurs IFRAP."
                className="w-full bg-[#1a1a2e] border border-gray-700 rounded-lg px-4 py-3 text-sm text-white outline-none resize-none focus:border-purple-500"
                rows={6}
                disabled={loading}
              />
            </div>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={(!prompt.trim() && mode !== 'transform') || (!contentText.trim() && mode === 'transform') || !isValidEmail || loading}
              className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white py-3.5 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-30 transition-colors"
              data-testid="gamma-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {mode === 'transform' ? 'Conversion en cours...' : 'Génération en cours (~45 sec)...'}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  {mode === 'gamma' ? 'Générer la présentation' : mode === 'report' ? 'Générer rapport + présentation' : 'Convertir en Gamma'}
                </>
              )}
            </button>

            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}
          </div>
        ) : (
          /* Success */
          <div className="bg-[#0f0f20] rounded-2xl border border-green-800/50 p-6 text-center">
            <div className="w-14 h-14 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-green-400" />
            </div>
            <h2 className="text-white font-bold text-lg mb-2">{result.title}</h2>
            <p className="text-gray-400 text-sm mb-4">{result.message}</p>
            
            {result.gamma_url && (
              <a
                href={result.gamma_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                <Presentation className="w-5 h-5" /> Ouvrir dans Gamma
              </a>
            )}

            <button
              onClick={() => { setResult(null); setPrompt(''); }}
              className="block mx-auto mt-4 text-gray-500 text-sm hover:text-gray-300 transition-colors"
            >
              Générer une autre présentation
            </button>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-gray-600 text-xs mt-6">
          CyberAgnès · Fondation IFRAP
        </p>
      </div>
    </div>
  );
};

export default GammaRemotePage;
